import { inject, Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngxs/store";
import { Alert, ApplicationState } from "@vp/data-access/application";
import { ContentData, SeverityLevel, User } from "@vp/models";
import { AlertItem } from "@vp/shared/components/alert";
import { DeviceInfo } from "ngx-device-detector";
import { EMPTY, merge, Observable, Subject, timer } from "rxjs";
import { map, switchMap, takeUntil, tap } from "rxjs/operators";
import * as SystemBannerActions from "../state/system-banner-action";
import { SystemBannerState } from "../state/system-banner-state";

@Injectable({
  providedIn: "root"
})
export class SystemBannerAdapter implements OnDestroy {
  private store = inject(Store);

  alerts$: Observable<Alert[]> = inject(Store).select(ApplicationState.alerts);
  loggedInUser$: Observable<User | null> = inject(Store).select(ApplicationState.loggedInUser);
  globalAlerts$ = inject(Store).select(SystemBannerState.globalAlerts);

  deviceInfo: DeviceInfo | undefined;

  private destroyed$ = new Subject<void>();

  constructor() {
    this.dismissExpiredAlerts();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  banners$ = this.globalAlerts$.pipe(
    takeUntil(this.destroyed$),
    map(alerts =>
      alerts.map(
        alert =>
          ({
            id: alert.contentId,
            type: this.mapAlertType(alert),
            message: alert.content,
            title: alert.displayName
          }) as AlertItem
      )
    )
  );

  dismissBanner(alertItem: AlertItem) {
    this.store.dispatch(new SystemBannerActions.DismissAlert(alertItem.id));
  }

  dismissExpiredAlerts() {
    this.globalAlerts$
      .pipe(
        switchMap(alerts => {
          const expiredAlerts = alerts.map(alert => {
            if (!alert.schedule?.end) {
              return EMPTY;
            }
            const delay = new Date(alert.schedule.end).getTime() - new Date().getTime();
            const delayHours = delay / 1000 / 60 / 60;
            if (delayHours >= 24) {
              return EMPTY;
            }
            return timer(delay).pipe(
              tap(() => {
                this.store.dispatch(new SystemBannerActions.DismissAlert(alert.contentId));
              })
            );
          });
          return merge(...expiredAlerts);
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();
  }

  mapAlertType(alert: ContentData) {
    switch (alert.severity) {
      case SeverityLevel.Warning:
        return "toast-warning";
      case SeverityLevel.Emergency:
        return "toast-error";
      case SeverityLevel.Informational:
        return "toast-info";
      default:
        return "toast-info";
    }
  }
}
